import { Plus, ShoppingBag, Store } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { useState } from 'react';
import { ACTIVITY_TYPE_META } from '@/lib/activities/activityTypeMeta';
import { useCreateActivitySheet } from '@/hooks/useCreateActivitySheet';
import { useProducerById } from '@/hooks/useMyProducers';

// Producer portal UX: Footer = areas (Overview, Shop, Events, Place, Contact). FAB = create.
// UX rule (navigation.md): FAB is the ONLY primary create action. Never used for navigation.
// UX rule (events.md): FAB on Events page opens type picker (Event / Kurs / Utflykt).
// v2: On overview, FAB also surfaces "Gå till gårdsbutik" when shop_enabled=true.

interface ProducerFABProps {
  farmId?: string;
}

type FabMode = 'menu' | 'product' | 'event' | 'hidden';

function useFabMode(farmId?: string): FabMode {
  const { pathname } = useLocation();
  if (!farmId) return 'hidden';
  const base = `/app/producer/farm/${farmId}`;

  // Hide FAB on product edit/create routes (sticky save bar takes over)
  if (pathname.startsWith(`${base}/shop/products/new`)) return 'hidden';
  if (/\/shop\/products\/[^/]+\/edit/.test(pathname)) return 'hidden';

  if (pathname.startsWith(`${base}/shop`)) return 'product';
  if (pathname.startsWith(`${base}/events`)) return 'event';
  if (pathname === base) return 'menu'; // dashboard overview
  // Plats, Kontakt, etc. → hidden
  return 'hidden';
}

const FAB_BUTTON_CLASS =
  'fixed right-4 z-50 h-14 w-14 rounded-full shadow-lg md:hidden';

export function ProducerFAB({ farmId }: ProducerFABProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const mode = useFabMode(farmId);
  const { openCreateMenu, CreateActivitySheet } = useCreateActivitySheet(farmId || '');
  const { producer } = useProducerById(farmId);

  const shopEnabled = producer?.shop_enabled ?? false;

  if (!isMobile || !farmId || mode === 'hidden') return null;

  const basePath = `/app/producer/farm/${farmId}`;

  // ── Direct-action: Gårdsbutik → new product ──
  if (mode === 'product') {
    return (
      <Button
        size="icon"
        aria-label="Ny produkt"
        className={FAB_BUTTON_CLASS}
        style={{ bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))' }}
        onClick={() => navigate(`${basePath}/shop/products/new`)}
      >
        <Plus className="h-6 w-6" />
      </Button>
    );
  }

  // ── Events: open type picker sheet (Event / Kurs / Utflykt) ──
  if (mode === 'event') {
    return (
      <>
        <Button
          size="icon"
          aria-label="Skapa aktivitet"
          data-testid="fab-create"
          className={FAB_BUTTON_CLASS}
          style={{ bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))' }}
          onClick={openCreateMenu}
        >
          <Plus className="h-6 w-6" />
        </Button>
        <CreateActivitySheet />
      </>
    );
  }

  // ── Overview dashboard: show all create options + shop navigation ──
  const em2 = ACTIVITY_TYPE_META.find(t => t.value === 'event')!;
  const actions = [
    ...(shopEnabled
      ? [{ label: 'Gå till gårdsbutik', description: 'Översikt, ordrar och produkter', icon: Store, href: `${basePath}/shop`, isNav: true }]
      : []),
    { label: 'Ny produkt', description: 'Lägg till en produkt i din gårdsbutik', icon: ShoppingBag, href: `${basePath}/shop/products/new`, isNav: false },
    { label: 'Ny aktivitet', description: em2.description, icon: em2.icon, href: `${basePath}/events/new?type=event`, isNav: false },
  ];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          size="icon"
          aria-label="Skapa ny"
          className={FAB_BUTTON_CLASS}
          style={{ bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))' }}
        >
          <Plus className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="rounded-t-2xl">
        <SheetHeader>
          <SheetTitle>Snabbåtgärder</SheetTitle>
        </SheetHeader>
        <div className="grid gap-2 py-4">
          {actions.map((action) => (
            <Button
              key={action.label}
              variant="ghost"
              className="justify-start gap-3 h-auto py-3 text-base"
              asChild
              onClick={() => setOpen(false)}
            >
              <Link to={action.href}>
                <action.icon className="h-5 w-5 shrink-0" />
                <div className="text-left">
                  <span className="block">{action.label}</span>
                  <span className="block text-sm font-normal text-muted-foreground">{action.description}</span>
                </div>
              </Link>
            </Button>
          ))}
        </div>
        <div className="bottom-sheet-spacer" />
      </SheetContent>
    </Sheet>
  );
}
